import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Star, Users, GraduationCap, MessageSquare, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

function Stars({ value = 0, onPick }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} disabled={!onPick} onClick={() => onPick?.(n)} data-testid={onPick ? `star-${n}` : undefined}
          className={onPick ? "cursor-pointer" : "cursor-default"}>
          <Star size={onPick ? 22 : 14} className={n <= Math.round(value) ? "text-brand fill-current" : "text-sec"} />
        </button>
      ))}
    </div>
  );
}

export default function Teachers() {
  const { user } = useApp();
  const [teachers, setTeachers] = useState([]);
  const [sel, setSel] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  useEffect(() => { api.get("/lms/teachers/trending").then(({ data }) => setTeachers(data.teachers || [])).catch(() => {}); }, []);
  const open = async (id) => {
    try { const { data } = await api.get(`/lms/teacher/${id}`); setSel(data); setRating(data.your_review?.rating || 0); setComment(data.your_review?.comment || ""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitReview = async () => {
    if (!rating) { toast.error("Pick a star rating."); return; }
    try { await api.post(`/lms/teacher/${sel.teacher_id}/review`, { rating, comment }); toast.success("Thanks for your feedback!"); open(sel.teacher_id); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 pt-24 pb-16" data-testid="teachers-page">
      <div className="flex items-center gap-2"><GraduationCap className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">Trending teachers</h1></div>
      <p className="text-sec text-sm mt-2">Top VishwaGuru-University gurus, ranked by learner love.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="trending-teachers">
        {teachers.length === 0 && <p className="text-sec text-sm">No teachers yet — be the first in Teacher Studio!</p>}
        {teachers.map((t) => (
          <button key={t.teacher_id} data-testid={`teacher-${t.teacher_id}`} onClick={() => open(t.teacher_id)} className="rud-card rounded-2xl p-5 text-left hover:-translate-y-1 hover:border-brand/50 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-elevated border border-rud grid place-items-center overflow-hidden">{t.picture ? <img src={t.picture} alt="" className="w-full h-full object-cover" /> : <span>{(t.name || "?")[0]}</span>}</div>
              <div><div className="font-medium">{t.name}</div><div className="text-xs text-sec flex items-center gap-1"><Star size={12} className="text-brand fill-current" /> {t.rating_avg || 0} · {t.rating_count} reviews</div></div>
            </div>
            {t.bio && <p className="text-sec text-sm mt-3 line-clamp-2">{t.bio}</p>}
            <div className="flex items-center gap-3 text-xs text-sec mt-3"><span className="flex items-center gap-1"><Users size={12} /> {t.students}</span><span>{t.courses_count} courses</span></div>
          </button>
        ))}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="rud-card rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="teacher-detail">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-elevated border border-rud grid place-items-center overflow-hidden">{sel.picture ? <img src={sel.picture} alt="" className="w-full h-full object-cover" /> : <span>{(sel.name || "?")[0]}</span>}</div>
                <div><div className="font-serif text-2xl">{sel.name}</div><div className="text-xs text-sec flex items-center gap-1"><Stars value={sel.rating_avg} /> {sel.rating_avg || 0} · {sel.rating_count} reviews</div></div>
              </div>
              <button onClick={() => setSel(null)}><X size={18} className="text-sec" /></button>
            </div>
            {sel.bio && <p className="text-sec text-sm mt-3">{sel.bio}</p>}
            {sel.subjects?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{sel.subjects.map((s, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-rud text-sec">{s}</span>)}</div>}

            {user && user.user_id !== sel.teacher_id && (
              <div className="rud-card rounded-xl p-4 mt-4" data-testid="review-form">
                <div className="text-sm font-medium mb-2">Rate this teacher</div>
                <Stars value={rating} onPick={setRating} />
                <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share a comment (optional)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
                <button data-testid="review-submit" onClick={submitReview} className="btn-primary rounded-full h-9 px-4 text-sm mt-2 flex items-center gap-1.5"><MessageSquare size={14} /> {sel.your_review ? "Update review" : "Post review"}</button>
              </div>
            )}

            <div className="mt-4 space-y-3">
              {(sel.reviews || []).map((r, i) => (
                <div key={i} className="border-t border-rud pt-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">{r.user_name}</span><Stars value={r.rating} /></div>
                  {r.comment && <p className="text-sec text-sm mt-1">{r.comment}</p>}
                </div>
              ))}
              {(sel.reviews || []).length === 0 && <p className="text-sec text-sm">No reviews yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
